import { IconClose16, IconUser16 } from '@warp-ds/icons/react';
import { Button } from '@warp-ds/react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { MetaDatePickerWrapper } from '../../components/metaDatePickerWrapper/MetaDatePickerWrapper';
import { useConfig } from '../../config/ConfigContext';
import { t } from '../../locale/texts';
import type { FlightSearchForm } from '../../types/search';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { flightAutocompleteMapper } from '../common/autocomplete/utils/AutocompleteMapper';
import { CustomRadioGroup } from '../common/CustomRadioGroup';
import { CustomSelect } from '../common/CustomSelect';
import { FrontPageModal } from '../common/frontPageModal/FrontPageModal';
import { storeLastSearchFlight } from '../common/lastSearch';
import { flightResultUrl as createFlightResultUrl } from '../common/resultUrl';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { trackSearchClick } from '../common/tracking';
import styles from './FlightSearch.module.css';
import actions, { reducer } from './flightActions';
import { flightDefaultState } from './flightDefaultState';
import { useFlightInitialState } from './hooks/useFlightInitialState';
import { useFlightValidation } from './hooks/useFlightValidation';
import { Passengers } from './Passenger';
import { buildFlightTrackingItem } from './utils/buildFlightTrackingItem';
import { cabinTypesOptions, tripTypesOptions } from './utils/flightSearchOptions';
import { validateSearchForm } from './utils/validation';

type FlightSearchProps = {
    prefill?: Partial<FlightSearchForm>;
    position: string;
    filters?: Record<string, unknown>;
    compactTop?: boolean;
    closeSearch?: () => void;
};

export const FlightSearch = ({ prefill, filters, position, closeSearch, compactTop }: FlightSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [form, dispatch] = useReducer(reducer, flightDefaultState);
    const [hasMounted, setHasMounted] = useState(false);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const { flightAutocompleteUrl, flightResultUrl } = useConfig();
    const { query } = useRouter();

    useFlightInitialState(dispatch, prefill, query);

    const { invalid, validationErrors } = useFlightValidation(form, pristine);

    const roundTrip = form.tripType === 'roundtrip';
    const openJaw = form.tripType === 'openjaw';

    const totalAmountOfPassengers = form.adults + form.children + form.infants;

    const cabinAndPassengersTriggerText = useMemo(() => {
        return `${t('flight.passengers.traveller', {
            count: totalAmountOfPassengers,
        })}, ${t(`flight.cabinType.${form.cabinType}`)}`;
    }, [form.adults, form.children, form.infants, form.cabinType]);

    const updateTripType = useCallback(
        (value: string) => {
            setPristine(value !== form.tripType);
            dispatch(actions.setTripType(value));
        },
        [form.tripType, dispatch],
    );

    const handleSubmit = (): void => {
        setPristine(false);

        if (validateSearchForm(form)) {
            storeLastSearchFlight(form);
            // Pulse tracker id: TR_ev01
            trackSearchClick('Search flight', '/reise', 'flight', position, buildFlightTrackingItem(form));
            window.location.assign(createFlightResultUrl({ state: form, filters: filters, url: flightResultUrl }));
        }
    };

    return (
        <SearchPanelWrapper
            dataTestId="flightSearch"
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
            compactTop={compactTop}>
            <div className="flex flex-row gap-12 sm:gap-8 justify-between">
                <div className="flex-row gap-12 sm:gap-8 hidden sm:flex">
                    <Passengers
                        type="dropdown"
                        setPassenger={(data) => dispatch(actions.setPassenger(data))}
                        trigger={{
                            text: t('flight.passengers.traveller', { count: totalAmountOfPassengers }),
                        }}
                        form={{
                            adults: form.adults,
                            children: form.children,
                            infants: form.infants,
                        }}
                    />
                    <CustomSelect
                        slim
                        options={cabinTypesOptions}
                        onChange={(cabinType) => dispatch(actions.setCabinType(cabinType))}
                        value={form.cabinType}
                    />
                    <CustomSelect slim options={tripTypesOptions} onChange={updateTripType} value={form.tripType} />
                </div>
                <CustomRadioGroup
                    options={tripTypesOptions}
                    value={form.tripType}
                    onChange={(e) => updateTripType(e.value)}
                    legend="Velg reise type"
                    hideLegend
                    className="sm:hidden"
                    dir="row"
                />
                {closeSearch && (
                    <Button
                        className={cn('hidden md:block')}
                        type="reset"
                        secondary
                        quiet
                        onClick={closeSearch}
                        aria-label={t('action.close')}>
                        <IconClose16 className="s-icon" aria-hidden />
                    </Button>
                )}
            </div>
            <div className={cn(styles['form-content'], 'gap-12 sm:gap-8 w-full')}>
                <div className="flex flex-col gap-12 sm:gap-8 w-full">
                    <div className={'flex flex-col md:flex-row w-full gap-12 sm:gap-8'}>
                        {openJaw && <div className={styles['route-divider']}>Rute 1</div>}
                        <fieldset className="flex flex-col sm:flex-row w-full gap-12 sm:gap-8">
                            <legend className="sr-only">Velg reise</legend>
                            <Autocomplete
                                vertical="flight"
                                id={`origin-${form.tripType}`}
                                label={t('flight.autocomplete.from.label')}
                                placeholder={t('flight.autocomplete.from.placeholder')}
                                defaultValue={form.originName}
                                url={flightAutocompleteUrl}
                                mapOptions={flightAutocompleteMapper}
                                error={validationErrors.origin}
                                onSelect={(option) => dispatch(actions.setOrigin(option))}
                            />
                            <Autocomplete
                                vertical="flight"
                                id={`destination-${form.tripType}`}
                                label={t('flight.autocomplete.to.label')}
                                placeholder={t('flight.autocomplete.to.placeholder')}
                                defaultValue={form.destinationName}
                                error={validationErrors.destination ?? validationErrors.sameOriginDestination}
                                url={flightAutocompleteUrl}
                                mapOptions={flightAutocompleteMapper}
                                onSelect={(option) => dispatch(actions.setDestination(option))}
                                departure={false}
                            />
                        </fieldset>
                        <MetaDatePickerWrapper
                            startDate={form.departureDate}
                            endDate={roundTrip ? form.returnDate : undefined}
                            invalid={invalid}
                            validationErrors={validationErrors}
                            form={form}
                            dispatch={dispatch}
                            openJawNumber={openJaw ? 0 : null}
                        />
                    </div>
                    {openJaw && (
                        <div className="flex flex-col md:flex-row w-full gap-12 sm:gap-8">
                            {openJaw && <div className={styles['route-divider']}>Rute 2</div>}
                            <fieldset className="flex flex-col sm:flex-row w-full gap-12 sm:gap-8">
                                <Autocomplete
                                    vertical="flight"
                                    id="origin-openjaw2"
                                    label={t('flight.autocomplete.from.label')}
                                    placeholder={t('flight.autocomplete.from.placeholder')}
                                    defaultValue={form.originName2}
                                    url={flightAutocompleteUrl}
                                    mapOptions={flightAutocompleteMapper}
                                    error={validationErrors.origin2}
                                    onSelect={(option) => dispatch(actions.setOrigin2(option))}
                                />
                                <Autocomplete
                                    vertical="flight"
                                    id="destination-openjaw2"
                                    label={t('flight.autocomplete.to.label')}
                                    placeholder={t('flight.autocomplete.to.placeholder')}
                                    defaultValue={form.destinationName2}
                                    error={validationErrors.destination2 ?? validationErrors.sameOriginDestination2}
                                    url={flightAutocompleteUrl}
                                    mapOptions={flightAutocompleteMapper}
                                    onSelect={(option) => dispatch(actions.setDestination2(option))}
                                    departure={false}
                                />
                            </fieldset>
                            <MetaDatePickerWrapper
                                isReturnPicker
                                dispatch={dispatch}
                                startDate={form.returnDate}
                                invalid={invalid}
                                validationErrors={validationErrors}
                                form={form}
                                openJawNumber={openJaw ? 1 : null}
                            />
                        </div>
                    )}
                </div>
                <FrontPageModal
                    title="Reisende og billettype"
                    breakpoint="sm"
                    trigger={{
                        text: cabinAndPassengersTriggerText,
                        prefix: <IconUser16 aria-hidden />,
                        aria: {
                            label: 'endre reisende og kabintype',
                        },
                    }}>
                    <Passengers
                        type="inline"
                        setPassenger={(data) => dispatch(actions.setPassenger(data))}
                        form={{
                            adults: form.adults,
                            children: form.children,
                            infants: form.infants,
                        }}
                    />
                    <CustomRadioGroup
                        options={cabinTypesOptions}
                        value={form.cabinType}
                        onChange={(e) => dispatch(actions.setCabinType(e.value))}
                        legend={t('flight.cabinType.title')}
                    />
                </FrontPageModal>
                <div
                    className={cn(styles['search-button-container'], {
                        [styles['search-button-container--openjaw']]: openJaw,
                        [styles['search-button-container--error']]: hasMounted && invalid && Object.values(invalid).some((v) => v),
                    })}>
                    <Button className={styles['search-button']} type="submit" primary data-testid="flightSearchButton">
                        {t('action.search')}
                    </Button>
                    {closeSearch && (
                        <Button className={cn(styles['search-button'], 'md:hidden')} type="reset" secondary onClick={closeSearch}>
                            {t('action.close')}
                        </Button>
                    )}
                </div>
            </div>
        </SearchPanelWrapper>
    );
};
